import {
  sumAiUnits,
  getBillableMode,
  computeDeficit,
  sumBucketChargesForGraph,
  countBucketChargesForGraph,
  formatMonthLabelKey
} from '../utils/dashboardMath'

// Charge checkboxes change QBO dollars on the chart; monthly buckets still list every line for the drawer.
const useDashboardGraph = ({
  projectData,
  projectGraphLabels,
  setGraphData,
  setGraphStatistics,
  catNames,
  clientSettings,
  graphFilters,
  selectedCharges,
  excludedChargeRowKeys
}) => {
  const handleDataChanges = (dataDiff, existingGraphData) => {
    const { projectDiff, dateRangeDiff, filterDiff, billableDiff, currentTab } = dataDiff

    if (
      !projectDiff || typeof projectDiff !== 'object' ||
      !filterDiff || typeof filterDiff !== 'object' ||
      !Array.isArray(dateRangeDiff) || dateRangeDiff.length !== 2
    ) return

    const labelLen = Array.isArray(projectGraphLabels) ? projectGraphLabels.length : 0
    if (!labelLen) return

    let startRange = Number(dateRangeDiff[0])
    let endRange = Number(dateRangeDiff[1])
    if (!Number.isFinite(startRange)) startRange = 0
    if (!Number.isFinite(endRange)) endRange = startRange

    startRange = Math.max(0, Math.min(startRange, labelLen - 1))
    endRange = Math.max(startRange, Math.min(endRange, labelLen - 1))

    // Walk visible axis labels so month keys stay aligned with `projectMonthlyData` (no off-by-one from date math).
    const dateArr = []
    for (let i = startRange; i <= endRange; i++) {
      const lbl = projectGraphLabels[i]
      const key = formatMonthLabelKey(lbl)
      if (!/^\d{4}-\d{2}$/.test(key)) continue
      dateArr.push({ key, label: lbl ?? key })
    }
    if (!dateArr.length) return

    const hasAnySupportTypeFilter = Object.values(filterDiff).some(Boolean)
    const billableMode = getBillableMode(billableDiff) // expected: 'billable' | 'nonbillable' (and we tolerate 'all' if it exists)

    const buildTabStats = () => ({
      Dollars: { sum: 0, avg: 0 },
      SP: { sum: 0, avg: 0 },
      Units: { sum: 0, avg: 0 },
      TicketCount: { sum: 0, avg: 0 },
      DollarsPerSP: { sum: 0, avg: 0 },
      DollarsPerUnit: { sum: 0, avg: 0 },
      UnitsPerItem: { sum: 0, avg: 0 },
      Items: { sum: 0, avg: 0 },
      Deficit: { sum: 0, avg: 0 },
      BillableUnits: { sum: 0 },
      NonBillableUnits: { sum: 0 },
      SupportUnits: { sum: 0 },
      UpgradesUnits: { sum: 0 },
      VulnerabilitiesUnits: { sum: 0 },
      DataPoints: { sum: 0 }
    })

    const splitBillableUnits = (items) => {
      const src = Array.isArray(items) ? items : []

      return src.reduce((acc, item) => {
        if (!item || typeof item !== 'object') return acc

        const units = Number(item.revisedSP) || 0
        if (units <= 0) return acc

        const type = item.supportType
        const isSupport = type === graphFilters.SUPPORT

        // Upgrades + Vulnerabilities are BILLABLE by business rules
        if (!isSupport) {
          acc.billableUnits += units
          if (type === graphFilters.UPGRADES) acc.upgradesUnits += units
          if (type === graphFilters.VULNERABILITIES) acc.vulnerabilitiesUnits += units
          return acc
        }

        acc.supportUnits += units
        if (item.isBillable === true) acc.billableUnits += units
        else acc.nonBillableUnits += units

        return acc
      }, { billableUnits: 0, nonBillableUnits: 0, supportUnits: 0, upgradesUnits: 0, vulnerabilitiesUnits: 0 })
    }

    const passBillable = (item) => {
      if (!item || typeof item !== 'object') return false

      const type = item.supportType
      const isSupport = type === graphFilters.SUPPORT

      // Upgrades + Vulnerabilities are always billable
      if (!isSupport) {
        // billable mode includes them, nonbillable mode excludes them
        if (billableMode === 'nonbillable') return false
        return true
      }

      // Only SUPPORT tickets participate in isBillable filtering
      if (billableMode === 'billable') return item.isBillable === true
      if (billableMode === 'nonbillable') return item.isBillable !== true

      return true
    }

    // Store per-month non-billable SP for chart rebuilding in non-billable mode
    const monthlyNonBillableSP = {}
    const allZDIdsForTicketSP = new Set()

    const { projectDatasets, tabStats } = Object.entries(projectDiff)
      .filter(([k, v]) => typeof k === 'string' && typeof v === 'boolean' && v)
      .reduce((obj, [projectName]) => {
        if (!projectData?.[projectName]) return obj

        const rangeData = dateArr.reduce((arr, { key }) => {
          const monthlyData = projectData[projectName]?.[key]

          if (!monthlyData || typeof monthlyData !== 'object') {
            arr.push(0)
            return arr
          }

          const dollars = Number(monthlyData.Dollars || 0)
          const chartQboDollars = sumBucketChargesForGraph(monthlyData, selectedCharges, excludedChargeRowKeys)
          const sp = Number(monthlyData.SP || 0)
          const ticketCount = Number(monthlyData.TicketCount || 0)

          const baseItems = [
            ...(Array.isArray(monthlyData.Tickets) ? monthlyData.Tickets : []),
            ...(Array.isArray(monthlyData.Upgrades) ? monthlyData.Upgrades : []),
            ...(Array.isArray(monthlyData.Vulnerabilities) ? monthlyData.Vulnerabilities : [])
          ]

          const typeFiltered = !hasAnySupportTypeFilter
            ? []
            : baseItems.filter((t) => {
              if (!t || typeof t !== 'object') return false
              const type = t.supportType
              if (!Object.prototype.hasOwnProperty.call(filterDiff, type)) return false
              return Boolean(filterDiff[type])
            })

          const items = typeFiltered.filter(passBillable)

          switch (currentTab) {
            case catNames.SP_MONTHLY: {
              obj.tabStats.DataPoints.sum += Number(Boolean(sp))
              obj.tabStats.Dollars.sum += dollars
              obj.tabStats.SP.sum += sp

              const val = dollars / (sp > 0 ? sp : 1)
              obj.tabStats.DollarsPerSP.sum += val
              arr.push(val)
              return arr
            }

            case catNames.BILLING: {
              const countThisMonth = Number(dollars !== 0 && Number.isFinite(dollars))
              obj.tabStats.DataPoints.sum += countThisMonth
              obj.tabStats.Dollars.sum += dollars
              arr.push(dollars)
              return arr
            }

            case catNames.AI_BILLING: {
              const countThisMonth = Number(chartQboDollars !== 0 && Number.isFinite(chartQboDollars))
              obj.tabStats.DataPoints.sum += countThisMonth
              obj.tabStats.Dollars.sum += chartQboDollars
              obj.tabStats.ChargeCount =
                (obj.tabStats.ChargeCount || 0) +
                countBucketChargesForGraph(monthlyData, selectedCharges, excludedChargeRowKeys)
              arr.push(chartQboDollars)
              return arr
            }

            case catNames.TICKETS_MONTHLY: {
              const countThisMonth = Number(ticketCount !== 0 && Number.isFinite(ticketCount))
              obj.tabStats.DataPoints.sum += countThisMonth
              obj.tabStats.TicketCount.sum += ticketCount
              arr.push(ticketCount)
              return arr
            }

            case catNames.TRACKING: {
              const countThisMonth = Number(sp !== 0 && Number.isFinite(sp))
              obj.tabStats.DataPoints.sum += countThisMonth
              obj.tabStats.SP.sum += sp
              arr.push(sp)
              return arr
            }

            case catNames.AI_SP_MONTHLY: {
              // Visible series units depend on billable mode
              const units = sumAiUnits(items, graphFilters)

              // Billable vs non-billable split (support only: support billable vs support non-billable; upgrades/vulns count as billable)
              const split = splitBillableUnits(typeFiltered)
              obj.tabStats.BillableUnits.sum += split.billableUnits
              obj.tabStats.NonBillableUnits.sum += split.nonBillableUnits
              obj.tabStats.SupportUnits.sum = (obj.tabStats.SupportUnits?.sum ?? 0) + split.supportUnits
              obj.tabStats.UpgradesUnits.sum = (obj.tabStats.UpgradesUnits?.sum ?? 0) + split.upgradesUnits
              obj.tabStats.VulnerabilitiesUnits.sum = (obj.tabStats.VulnerabilitiesUnits?.sum ?? 0) + split.vulnerabilitiesUnits

              if (billableMode === 'nonbillable') {
                // Store per-month non-billable SP for later chart rebuild
                if (!monthlyNonBillableSP[projectName]) monthlyNonBillableSP[projectName] = []
                monthlyNonBillableSP[projectName].push(split.nonBillableUnits)

                const hasPoint = Number(Boolean(split.nonBillableUnits))
                obj.tabStats.DataPoints.sum += hasPoint

                obj.tabStats.Dollars.sum += chartQboDollars
                obj.tabStats.Units.sum += split.nonBillableUnits

                // Push placeholder, will be recalculated after effective rate is known
                arr.push(0)
                return arr
              }

              const hasPoint = Number(Boolean(units))
              obj.tabStats.DataPoints.sum += hasPoint

              obj.tabStats.Dollars.sum += chartQboDollars
              obj.tabStats.Units.sum += units

              const val = chartQboDollars / (units > 0 ? units : 1)
              obj.tabStats.DollarsPerUnit.sum += val

              const deficit = computeDeficit(chartQboDollars, split.billableUnits, split.nonBillableUnits)
              obj.tabStats.Deficit.sum += deficit

              arr.push(val)
              return arr
            }

            case catNames.AI_TRACKING: {
              const units = sumAiUnits(items, graphFilters)
              const split = splitBillableUnits(items)

              obj.tabStats.DataPoints.sum += Number(Boolean(units))
              obj.tabStats.Units.sum += units
              obj.tabStats.BillableUnits.sum = (obj.tabStats.BillableUnits?.sum ?? 0) + split.billableUnits
              obj.tabStats.NonBillableUnits.sum = (obj.tabStats.NonBillableUnits?.sum ?? 0) + split.nonBillableUnits
              obj.tabStats.SupportUnits.sum = (obj.tabStats.SupportUnits?.sum ?? 0) + split.supportUnits
              obj.tabStats.UpgradesUnits.sum = (obj.tabStats.UpgradesUnits?.sum ?? 0) + split.upgradesUnits
              obj.tabStats.VulnerabilitiesUnits.sum = (obj.tabStats.VulnerabilitiesUnits?.sum ?? 0) + split.vulnerabilitiesUnits

              arr.push(units)
              return arr
            }

            case catNames.AI_TICKET_SP: {
              const units = sumAiUnits(items, graphFilters)
              const supportItems = items.filter(i => i?.supportType === graphFilters.SUPPORT)
              supportItems.forEach((i) => {
                const id = i?.zendeskID
                if (id != null && id !== '') allZDIdsForTicketSP.add(String(id))
              })
              const zdCount = new Set(supportItems.map(i => i?.zendeskID).filter(id => id != null && id !== '')).size || 1
              const perItem = units / zdCount

              obj.tabStats.DataPoints.sum += Number(Boolean(units))
              obj.tabStats.UnitsPerItem.sum += perItem

              arr.push(perItem)
              return arr
            }

            default:
              arr.push(0)
              return arr
          }
        }, [])

        obj.projectDatasets.push({
          label: projectName,
          data: rangeData,
          fill: false,
          tension: 0.1,
          borderColor: clientSettings?.[projectName]?.color || `#${Math.floor(Math.random() * 16777215).toString(16)}`
        })

        return obj
      }, {
        tabStats: buildTabStats(),
        projectDatasets: []
      })

    const points = Number(tabStats.DataPoints.sum || 0) || 1

    tabStats.Dollars.avg = tabStats.Dollars.sum / points
    tabStats.SP.avg = tabStats.SP.sum / points
    tabStats.Units.avg = tabStats.Units.sum / points
    tabStats.TicketCount.avg = tabStats.TicketCount.sum / points
    tabStats.Items.avg = tabStats.Items.sum / points
    tabStats.Deficit.avg = tabStats.Deficit.sum / points

    if (currentTab === catNames.SP_MONTHLY) {
      const denom = tabStats.SP.sum > 0 ? tabStats.SP.sum : 1
      tabStats.DollarsPerSP.avg = tabStats.Dollars.sum / denom
      tabStats.DollarsPerSP.sum = tabStats.DollarsPerSP.avg
    }

    if (currentTab === catNames.AI_SP_MONTHLY) {
      const billableSum = Number(tabStats.BillableUnits?.sum ?? 0) || 0
      const nonBillableSum = Number(tabStats.NonBillableUnits?.sum ?? 0) || 0
      const totalSP = billableSum + nonBillableSum
      const effectiveSP = totalSP > 0 ? totalSP : (tabStats.Dollars.sum > 0 ? 1 : 0)
      tabStats.EffectiveDollarsPerSP = effectiveSP > 0 ? tabStats.Dollars.sum / effectiveSP : 0

      if (billableMode === 'nonbillable') {
        // Aggregate deficit = effective $/SP × total non-billable SP (for "Revenue lost")
        const revenueLost = tabStats.EffectiveDollarsPerSP * nonBillableSum
        tabStats.Deficit.sum = revenueLost
        tabStats.Deficit.avg = points > 0 ? tabStats.Deficit.sum / points : 0
        tabStats.DeficitDollarsPerSP = nonBillableSum > 0 ? revenueLost / nonBillableSum : 0
        // Deficit (total) = percentage: revenue lost / total revenue
        tabStats.DollarsPerUnit.sum = tabStats.Dollars.sum > 0 ? (revenueLost / tabStats.Dollars.sum) * 100 : 0
        tabStats.DollarsPerUnit.avg = tabStats.DollarsPerUnit.sum

        // Rebuild chart data using global effective rate × per-month non-billable SP
        projectDatasets.forEach((dataset) => {
          const projectName = dataset.label
          const monthlySP = monthlyNonBillableSP[projectName] || []
          if (monthlySP.length === dataset.data.length) {
            dataset.data = monthlySP.map((sp) => tabStats.EffectiveDollarsPerSP * sp)
          }
        })
      } else {
        // Total $/SP = Effective $/SP × (Billable SP + Non-billable SP)
        tabStats.DollarsPerUnit.sum = tabStats.EffectiveDollarsPerSP * (billableSum + nonBillableSum)
        tabStats.DollarsPerUnit.avg = tabStats.DollarsPerUnit.sum
        tabStats.DollarsPerUnit.avgPerMonth = points > 0 ? tabStats.DollarsPerUnit.sum / points : 0
      }

      const totalUnits = billableSum + nonBillableSum
      const supportSum = Number(tabStats.SupportUnits?.sum ?? 0) || 0
      const upgradesSum = Number(tabStats.UpgradesUnits?.sum ?? 0) || 0
      const vulnerabilitiesSum = Number(tabStats.VulnerabilitiesUnits?.sum ?? 0) || 0
      tabStats.UnitsPercentages = totalUnits > 0
        ? {
            billablePct: (billableSum / totalUnits) * 100,
            nonBillablePct: (nonBillableSum / totalUnits) * 100,
            billableSum,
            nonBillableSum,
            supportSum,
            upgradesSum,
            vulnerabilitiesSum,
            supportPct: (supportSum / totalUnits) * 100,
            upgradesPct: (upgradesSum / totalUnits) * 100,
            vulnerabilitiesPct: (vulnerabilitiesSum / totalUnits) * 100
          }
        : null
    }

    if (currentTab === catNames.AI_TRACKING) {
      const totalUnits = Number(tabStats.Units?.sum ?? 0) || 0
      const billableSum = Number(tabStats.BillableUnits?.sum ?? 0) || 0
      const nonBillableSum = Number(tabStats.NonBillableUnits?.sum ?? 0) || 0
      const supportSum = Number(tabStats.SupportUnits?.sum ?? 0) || 0
      const upgradesSum = Number(tabStats.UpgradesUnits?.sum ?? 0) || 0
      const vulnerabilitiesSum = Number(tabStats.VulnerabilitiesUnits?.sum ?? 0) || 0
      tabStats.UnitsPercentages = totalUnits > 0
        ? {
            billablePct: (billableSum / totalUnits) * 100,
            nonBillablePct: (nonBillableSum / totalUnits) * 100,
            billableSum,
            nonBillableSum,
            supportSum,
            upgradesSum,
            vulnerabilitiesSum,
            supportPct: (supportSum / totalUnits) * 100,
            upgradesPct: (upgradesSum / totalUnits) * 100,
            vulnerabilitiesPct: (vulnerabilitiesSum / totalUnits) * 100
          }
        : null
    }

    if (currentTab === catNames.AI_TICKET_SP) {
      tabStats.UnitsPerItem.avg = tabStats.UnitsPerItem.sum / points
      tabStats.Items.sum = allZDIdsForTicketSP.size
      tabStats.Items.avg = allZDIdsForTicketSP.size / (points || 1)
    }

    setGraphData({
      ...(existingGraphData || {}),
      [currentTab]: {
        labels: dateArr.map((d) => d.label),
        datasets: projectDatasets
      }
    })

    setGraphStatistics((prev) => {
      return {
        ...(prev || {}),
        [currentTab]: {
          ...tabStats,
          BillableMode: billableMode
        }
      }
    })
  }

  return { handleDataChanges }
}

export { useDashboardGraph }
